// src/components/Effects/AmbientAudio.js
import React, { useRef, useState, useEffect } from 'react';

export default function AmbientAudio() {
  const [muted, setMuted] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const audioContextRef = useRef(null);
  const nodesRef = useRef({});

  function initAudio() {
    if (initialized) {
      setMuted(!muted);
      if (nodesRef.current.gainNode) {
        nodesRef.current.gainNode.gain.setValueAtTime(
          muted ? 0.03 : 0,
          audioContextRef.current.currentTime
        );
      }
      return;
    }

    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;

      // Create a low hum (60Hz)
      const hum = ctx.createOscillator();
      hum.type = 'sine';
      hum.frequency.setValueAtTime(60, ctx.currentTime);

      // Create subtle static noise
      const bufferSize = 2 * ctx.sampleRate;
      const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
      }
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;
      noise.loop = true;

      // Gain nodes
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0.03, ctx.currentTime);

      const humGain = ctx.createGain();
      humGain.gain.setValueAtTime(0.4, ctx.currentTime);

      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.15, ctx.currentTime);

      // Low-pass filter on noise for that CRT hiss
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(800, ctx.currentTime);

      // Connect
      hum.connect(humGain);
      humGain.connect(masterGain);

      noise.connect(filter);
      filter.connect(noiseGain);
      noiseGain.connect(masterGain);

      masterGain.connect(ctx.destination);

      hum.start();
      noise.start();

      nodesRef.current = { gainNode: masterGain, hum, noise };
      setInitialized(true);
      setMuted(false);
    } catch (e) {
      console.log('Audio not supported');
    }
  }

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <button
      onClick={initAudio}
      style={{
        position: 'fixed',
        bottom: '8px',
        right: '15px',
        background: 'none',
        border: '1px solid #333',
        color: muted ? '#555' : '#00ff41',
        fontFamily: "'VT323', monospace",
        fontSize: '14px',
        padding: '3px 8px',
        cursor: 'pointer',
        zIndex: 1001,
      }}
      title={muted ? 'Enable ambient audio' : 'Mute audio'}
    >
      {muted ? '♪ MUTED' : '♪ AUDIO ON'}
    </button>
  );
}